import { useState } from "react";
import { FileText, Image, Code, Download, ExternalLink, ChevronDown, ChevronUp } from "lucide-react";

interface FileItem {
  name: string;
  description?: string;
  url?: string;
  content?: string;
  mime_type?: string;
  size_bytes?: number;
  language?: string;
}

interface PresentFilesData {
  title?: string;
  files: FileItem[];
}

interface Props {
  data: PresentFilesData;
}

function FileIcon({ mime }: { mime?: string }) {
  if (!mime) return <FileText size={16} />;
  if (mime.startsWith("image/")) return <Image size={16} />;
  if (mime.includes("javascript") || mime.includes("typescript") || mime.includes("python") || mime.includes("json"))
    return <Code size={16} />;
  return <FileText size={16} />;
}

function formatSize(bytes?: number): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function FileCard({ file }: { file: FileItem }) {
  const [expanded, setExpanded] = useState(false);
  const isImage = file.mime_type?.startsWith("image/");
  const hasContent = !!file.content;

  return (
    <div className="present-file-card">
      <div className="present-file-header">
        <div className="present-file-icon">
          <FileIcon mime={file.mime_type} />
        </div>
        <div className="present-file-info">
          <span className="present-file-name">{file.name}</span>
          {file.description && (
            <span className="present-file-desc">{file.description}</span>
          )}
          {file.size_bytes && (
            <span className="present-file-size">{formatSize(file.size_bytes)}</span>
          )}
        </div>
        <div className="present-file-actions">
          {file.url && (
            <a href={file.url} download={file.name} className="present-file-btn" title="Last ned">
              <Download size={14} />
            </a>
          )}
          {file.url && (
            <a href={file.url} target="_blank" rel="noopener noreferrer" className="present-file-btn" title="Åpne">
              <ExternalLink size={14} />
            </a>
          )}
          {hasContent && (
            <button
              className="present-file-btn"
              onClick={() => setExpanded(e => !e)}
              title={expanded ? "Skjul innhold" : "Vis innhold"}
            >
              {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          )}
        </div>
      </div>

      {isImage && file.url && (
        <div className="present-file-preview">
          <img src={file.url} alt={file.name} className="present-file-image" />
        </div>
      )}

      {hasContent && expanded && (
        <div className="present-file-content">
          <pre className={`present-file-code${file.language ? ` language-${file.language}` : ""}`}>
            <code>{file.content}</code>
          </pre>
        </div>
      )}
    </div>
  );
}

export default function PresentFiles({ data }: Props) {
  return (
    <div className="tool-present-files">
      {data.title && <div className="present-files-title">{data.title}</div>}
      <div className="present-files-list">
        {data.files.map((file, i) => (
          <FileCard key={i} file={file} />
        ))}
      </div>
    </div>
  );
}
